import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import { messageFromError } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isAdminMembership,
  isActiveMembership,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import { useCompanyFocusRevalidation } from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";

import { fetchCompanyProperties } from "../api/companyProperties";
import {
  catalogPublicationLabel,
  coverImageUrl,
  dealTypeLabel,
  formatPropertyPrice,
  isCorporatePropertiesAvailable,
  propertyDisplayTitle,
  propertyStatusLabel,
  propertyTypeLabel,
  safeSettlementLocationLabel,
  userDisplayName,
} from "../lib/propertyLabels";
import {
  buildCorporatePropertyListParams,
  PROPERTY_FILTER_STATUSES,
  PROPERTY_FILTER_TYPES,
  readCorporatePropertyListQuery,
  writeCorporatePropertyListSearch,
} from "../lib/listQuery";

function unwrapPage(data) {
  if (Array.isArray(data)) {
    return { results: data, count: data.length, next: null, previous: null };
  }
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    count: typeof data?.count === "number" ? data.count : 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

function CompanyPropertiesListContent() {
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);

  const queryState = readCorporatePropertyListQuery(location.search);
  const queryKey = [
    queryState.query,
    queryState.propertyType || "",
    queryState.status || "",
    String(queryState.page),
  ].join("|");
  const [draftQuery, setDraftQuery] = useState(queryState.query);
  const [draftType, setDraftType] = useState(queryState.propertyType);
  const [draftStatus, setDraftStatus] = useState(queryState.status);

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  stateRef.current = state;
  const [error, setError] = useState(null);

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const canReadInventory =
    authorityMatches && isActiveMembership(company, membership);
  const canUseWrites =
    authorityMatches && isCorporatePropertiesAvailable(company);
  const canCreate =
    canUseWrites && isActiveMembership(company, membership);
  const canManageQueue =
    canUseWrites && isAdminMembership(company, membership);
  const canImport = canManageQueue;

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({ companyId, actorId });
    if (!keepVisible) {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineCompanyMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId);
      const mineMatches = mineCompanyMatches ? mine.membership : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);

      if (!mineCompanyMatches || !isActiveMembership(companyData, mineMatches)) {
        setRows([]);
        setState("denied");
        return;
      }

      const page = await fetchCompanyProperties(
        companyId,
        buildCorporatePropertyListParams({
          query: queryState.query,
          propertyType: queryState.propertyType,
          status: queryState.status,
          page: queryState.page,
        }),
      );
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const unwrapped = unwrapPage(page);
      setRows(unwrapped.results);
      setCount(unwrapped.count);
      setNextUrl(unwrapped.next);
      setPrevUrl(unwrapped.previous);
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        return;
      }
      setRows([]);
      if (status === 404) {
        setState("not-found");
      } else if (status === 403) {
        setState("denied");
      } else {
        setError(messageFromError(err));
        setState("error");
      }
    }
  }, [actorId, companyId, queryKey, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyOpRef.current = null;
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setRows([]);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId, queryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDraftQuery(queryState.query);
    setDraftType(queryState.propertyType);
    setDraftStatus(queryState.status);
  }, [queryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: false,
    onRevalidate: () => load({ background: true }),
  });

  const applyFilters = (event) => {
    event.preventDefault();
    navigate({
      pathname: `/companies/${companyId}/properties`,
      search: writeCorporatePropertyListSearch({
        query: draftQuery,
        propertyType: draftType,
        status: draftStatus,
        page: 1,
      }),
    });
  };

  const selectSingle = (current, value, setter) => {
    setter(current === value ? "" : value);
  };

  const totalPages = Math.max(1, Math.ceil(count / 20) || 1);

  return (
    <section
      className="clients-page feature-company-workspace feature-company-properties"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
        <CompanyPageHeader
          title="Об'єкти компанії"
          subtitle="Корпоративний інвентар · Компанія"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="properties"
          actions={
            state === "ready" && (canCreate || canManageQueue) ? (
              <>
                {canManageQueue ? (
                  <Link
                    to={`/companies/${companyId}/properties/unassigned`}
                    className="action-btn"
                  >
                    Непризначені об'єкти
                  </Link>
                ) : null}
                {canImport ? (
                  <Link
                    to={`/companies/${companyId}/properties/import`}
                    className="action-btn"
                  >
                    Імпорт
                  </Link>
                ) : null}
                {canCreate ? (
                  <Link
                    to={`/companies/${companyId}/properties/new`}
                    className="btn-more"
                  >
                    Новий об'єкт
                  </Link>
                ) : null}
              </>
            ) : null
          }
        />

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}

        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Активне членство в цій компанії потрібне для перегляду об'єктів."
          />
        ) : null}

        {state === "not-found" ? (
          <EmptyState
            title="Не знайдено"
            description="Компанію або ресурс не знайдено."
          />
        ) : null}

        {state === "error" ? (
          <EmptyState
            title="Помилка завантаження"
            description={error || "Спробуйте оновити сторінку."}
            primaryAction={
              <button type="button" className="action-btn" onClick={load}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && canReadInventory ? (
          <div className="clients-workspace">
            <form
              className="clients-toolbar"
              onSubmit={applyFilters}
              aria-label="Фільтри об'єктів компанії"
            >
              <div className="clients-toolbar__row">
                <input
                  className="clients-search"
                  type="search"
                  value={draftQuery}
                  onChange={(e) => setDraftQuery(e.target.value)}
                  placeholder="Пошук"
                  aria-label="Пошук"
                />
                <button type="submit" className="action-btn">
                  Знайти
                </button>
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  onClick={() => {
                    setDraftQuery("");
                    setDraftType("");
                    setDraftStatus("");
                    navigate(`/companies/${companyId}/properties`);
                  }}
                >
                  Скинути
                </button>
              </div>
              <div
                className="clients-filter-group feature-company-properties__filter-group"
                aria-label="Тип"
              >
                {PROPERTY_FILTER_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={[
                      "filter-chip",
                      draftType === t ? "filter-chip--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-pressed={draftType === t}
                    onClick={() => selectSingle(draftType, t, setDraftType)}
                  >
                    {propertyTypeLabel(t)}
                  </button>
                ))}
              </div>
              <div
                className="clients-filter-group feature-company-properties__filter-group"
                aria-label="Статус"
              >
                {PROPERTY_FILTER_STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={[
                      "filter-chip",
                      draftStatus === s ? "filter-chip--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-pressed={draftStatus === s}
                    onClick={() =>
                      selectSingle(draftStatus, s, setDraftStatus)
                    }
                  >
                    {propertyStatusLabel(s)}
                  </button>
                ))}
              </div>
            </form>

            {rows.length === 0 ? (
              <EmptyState
                title="Немає об'єктів"
                description="У видимому інвентарі компанії ще немає об'єктів."
              />
            ) : (
              <>
                <div className="clients-table-wrap" role="region" aria-label="Об'єкти компанії">
                  <table className="clients-table">
                    <thead>
                      <tr>
                        <th scope="col">
                          <span className="visually-hidden">Обкладинка</span>
                        </th>
                        <th scope="col">Назва</th>
                        <th scope="col">Тип</th>
                        <th scope="col">Угода</th>
                        <th scope="col">Ціна</th>
                        <th scope="col">Населений пункт</th>
                        <th scope="col">Відповідальний</th>
                        <th scope="col">Робочий статус</th>
                        <th scope="col">Каталог</th>
                        <th scope="col">
                          <span className="visually-hidden">Дії</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const coverSrc = coverImageUrl(row);
                        return (
                          <tr key={row.id}>
                            <td data-label="Обкладинка">
                              {coverSrc ? (
                                <img
                                  src={coverSrc}
                                  alt=""
                                  width={40}
                                  height={40}
                                />
                              ) : (
                                "—"
                              )}
                            </td>
                            <td data-label="Назва">
                              <Link
                                to={`/companies/${companyId}/properties/${row.id}`}
                              >
                                {propertyDisplayTitle(row)}
                              </Link>
                              <span className="feature-company-workspace__meta">
                                {" "}
                                · Компанія
                              </span>
                            </td>
                            <td data-label="Тип">
                              {propertyTypeLabel(row.property_type)}
                            </td>
                            <td data-label="Угода">
                              {dealTypeLabel(row.deal_type)}
                            </td>
                            <td data-label="Ціна">{formatPropertyPrice(row)}</td>
                            <td data-label="Населений пункт">
                              {safeSettlementLocationLabel(row.location)}
                            </td>
                            <td data-label="Відповідальний">
                              {userDisplayName(row.responsible_user)}
                            </td>
                            <td data-label="Робочий статус">
                              {propertyStatusLabel(
                                row.status || row.work_status,
                              )}
                            </td>
                            <td data-label="Каталог">
                              {catalogPublicationLabel(row.catalog_publication)}
                            </td>
                            <td data-label="Дії">
                              <Link
                                to={`/companies/${companyId}/properties/${row.id}`}
                                className="action-btn action-btn--ghost"
                              >
                                Відкрити
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <NumberedPagination
                  currentPage={queryState.page}
                  totalPages={totalPages}
                  hasNext={Boolean(nextUrl)}
                  hasPrevious={Boolean(prevUrl)}
                  ariaLabel="Сторінки об'єктів компанії"
                  onPageChange={(nextPage) => {
                    navigate({
                      pathname: `/companies/${companyId}/properties`,
                      search: writeCorporatePropertyListSearch({
                        query: queryState.query,
                        propertyType: queryState.propertyType,
                        status: queryState.status,
                        page: nextPage,
                      }),
                    });
                  }}
                />
              </>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default CompanyPropertiesListContent;
